import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

function cameraErrorMessage(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return "Accès à la caméra refusé. Vérifiez les autorisations du navigateur ou du site, puis réessayez.";
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Aucune caméra détectée sur cet appareil. Utilisez la saisie manuelle.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'La caméra est utilisée par une autre application. Fermez-la ou utilisez la saisie manuelle.';
  }
  if (name === 'OverconstrainedError') {
    return 'Caméra incompatible avec les contraintes demandées. Réessayez ou utilisez la saisie manuelle.';
  }
  return "Impossible d'accéder à la caméra. Utilisez la saisie manuelle.";
}

async function getCameraStream() {
  const constraintsList = [
    { video: { facingMode: { ideal: 'environment' } } },
    { video: { facingMode: 'environment' } },
    { video: true },
    { video: { facingMode: 'user' } },
  ];
  let lastErr;
  for (const constraints of constraintsList) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('getUserMedia failed');
}

/**
 * @param {{ onScan: (data: string) => void; layout?: 'default' | 'workshop' }} props
 */
const QRScanner = ({ onScan, layout = 'default' }) => {
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const scanningActiveRef = useRef(false);

  const stopTracksAndStream = useCallback(() => {
    scanningActiveRef.current = false;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const stopScanning = useCallback(() => {
    stopTracksAndStream();
    setScanning(false);
  }, [stopTracksAndStream]);

  useEffect(() => {
    return () => {
      stopTracksAndStream();
    };
  }, [stopTracksAndStream]);

  const scanLoop = useCallback(() => {
    if (!scanningActiveRef.current || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (code?.data) {
        setLastScan({ data: code.data, timestamp: new Date() });
        stopTracksAndStream();
        setScanning(false);
        onScan(code.data);
        return;
      }
    }

    rafRef.current = requestAnimationFrame(scanLoop);
  }, [onScan, stopTracksAndStream]);

  useEffect(() => {
    if (!scanning) return undefined;

    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return undefined;

    video.muted = true;
    video.setAttribute('playsinline', '');
    video.playsInline = true;
    video.srcObject = stream;

    let cancelled = false;

    const run = async () => {
      try {
        await video.play();
        if (cancelled) return;
        scanningActiveRef.current = true;
        rafRef.current = requestAnimationFrame(scanLoop);
      } catch (err) {
        console.error('video.play failed:', err);
        if (!cancelled) {
          setError(cameraErrorMessage(err));
          stopTracksAndStream();
          setScanning(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      scanningActiveRef.current = false;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      video.srcObject = null;
    };
  }, [scanning, scanLoop, stopTracksAndStream]);

  const startScanning = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Ce navigateur ne permet pas l'accès à la caméra. Utilisez la saisie manuelle.");
      return;
    }
    setError(null);
    try {
      const stream = await getCameraStream();
      streamRef.current = stream;
      setScanning(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(cameraErrorMessage(err));
      streamRef.current = null;
    }
  };

  const handleScanSuccess = (data) => {
    setLastScan({
      data,
      timestamp: new Date(),
    });
    stopScanning();
    onScan(data);
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualInput?.trim()) {
      handleScanSuccess(manualInput.trim());
      setManualInput('');
    }
  };

  const isWorkshop = layout === 'workshop';
  const gridClass = isWorkshop ? 'grid grid-cols-1 gap-4 min-w-0' : 'grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 min-w-0';

  return (
    <div className={gridClass}>
      <div className={`bg-card border border-border rounded-lg ${isWorkshop ? 'p-3' : 'p-4 md:p-6'}`}>
        <h3 className={`font-semibold text-foreground mb-3 ${isWorkshop ? 'text-sm' : 'text-base md:text-lg mb-4'}`}>
          Scanner QR code
        </h3>

        <div className="relative bg-black rounded-lg overflow-hidden mb-3" style={{ aspectRatio: '4/3' }}>
          <video
            ref={videoRef}
            className={`w-full h-full object-cover ${scanning ? 'block' : 'hidden'}`}
            playsInline
            muted
            autoPlay
            aria-hidden={!scanning}
          />
          <canvas ref={canvasRef} className="hidden" aria-hidden />
          {scanning ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-44 h-44 sm:w-52 sm:h-52 border-4 border-primary rounded-lg animate-pulse" />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/40">
              <div className="text-center px-4">
                <Icon name="Camera" size={isWorkshop ? 48 : 64} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Caméra inactive</p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-error/10 border border-error/30 rounded-lg p-3 mb-3 text-sm text-error">{error}</div>
        )}

        <div className="flex gap-2">
          {!scanning ? (
            <Button
              variant="primary"
              size="lg"
              iconName="Camera"
              onClick={startScanning}
              className="flex-1 min-h-[44px] min-touch-target"
            >
              Démarrer le scan
            </Button>
          ) : (
            <Button
              variant="outline"
              size="lg"
              iconName="X"
              onClick={stopScanning}
              className="flex-1 min-h-[44px] min-touch-target"
            >
              Arrêter le scan
            </Button>
          )}
        </div>
      </div>

      <div className={`space-y-4 min-w-0 ${isWorkshop ? '' : 'md:space-y-6'}`}>
        <div className={`bg-card border border-border rounded-lg ${isWorkshop ? 'p-3' : 'p-4 md:p-6'}`}>
          <h3 className={`font-semibold text-foreground mb-3 ${isWorkshop ? 'text-sm' : 'text-base md:text-lg mb-4'}`}>
            Saisie manuelle
          </h3>
          <form onSubmit={handleManualSubmit} className="space-y-3">
            <Input
              type="text"
              placeholder="URL ou code du lot"
              value={manualInput}
              onChange={(e) => setManualInput(e?.target?.value)}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              iconName="Check"
              disabled={!manualInput?.trim()}
              className="w-full min-h-[44px] min-touch-target"
            >
              Valider le code
            </Button>
          </form>
        </div>

        {lastScan && (
          <div className={`bg-card border border-border rounded-lg ${isWorkshop ? 'p-3' : 'p-4 md:p-6'}`}>
            <h3 className={`font-semibold text-foreground mb-2 ${isWorkshop ? 'text-sm' : 'text-base md:text-lg mb-4'}`}>
              Dernier scan
            </h3>
            <div className="bg-success/10 border border-success/30 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs sm:text-sm text-foreground break-all">{lastScan?.data}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {lastScan?.timestamp?.toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isWorkshop ? (
          <button
            type="button"
            onClick={() => setInstructionsOpen((o) => !o)}
            className="w-full flex items-center justify-between min-h-[44px] px-3 rounded-lg border border-border bg-card text-left text-sm font-medium text-foreground"
          >
            <span>Aide scan</span>
            <Icon name={instructionsOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
          </button>
        ) : null}
        {(!isWorkshop || instructionsOpen) && (
          <div className={`bg-card border border-border rounded-lg ${isWorkshop ? 'p-3' : 'p-4 md:p-6'}`}>
            <h3 className={`font-semibold text-foreground mb-2 ${isWorkshop ? 'text-sm' : 'text-base md:text-lg mb-4'}`}>
              Instructions
            </h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary mt-0.5 shrink-0" />
                <span>Placez le QR dans le cadre, de préférence avec la caméra arrière.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary mt-0.5 shrink-0" />
                <span>Le décodage est automatique dès que l’image est nette.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary mt-0.5 shrink-0" />
                <span>Si besoin, collez l’URL ou le texte du code ci-dessus.</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScanner;
